import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const DealsFilters = ({ 
  filters, 
  onFiltersChange, 
  onClearFilters, 
  dealCount,
  onBulkAction,
  selectedCount 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  const stageOptions = [
    { value: '', label: 'All Stages' },
    { value: 'New', label: 'New' },
    { value: 'Qualified', label: 'Qualified' },
    { value: 'Proposal', label: 'Proposal' },
    { value: 'Negotiation', label: 'Negotiation' },
    { value: 'Won', label: 'Won' },
    { value: 'Lost', label: 'Lost' }
  ];

  const ownerOptions = [
    { value: '', label: 'All Owners' },
    { value: 'Sarah Johnson', label: 'Sarah Johnson' },
    { value: 'Michael Chen', label: 'Michael Chen' },
    { value: 'Emily Rodriguez', label: 'Emily Rodriguez' },
    { value: 'David Kim', label: 'David Kim' },
    { value: 'Lisa Thompson', label: 'Lisa Thompson' }
  ];

  const bulkActions = [
    { value: 'stage', label: 'Update Stage', icon: 'GitBranch' },
    { value: 'owner', label: 'Reassign Owner', icon: 'User' },
    { value: 'export', label: 'Export Selected', icon: 'Download' },
    { value: 'delete', label: 'Delete Selected', icon: 'Trash2' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const handleBulkActionSelect = (action) => {
    onBulkAction(action);
    setShowBulkActions(false);
  };

  const activeFiltersCount = Object.values(filters)?.filter(value => 
    value !== '' && value !== null && value !== undefined
  )?.length;

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6">
      {/* Header Row */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-semibold text-foreground">
            Deals ({dealCount?.toLocaleString()})
          </h2>
          {activeFiltersCount > 0 && (
            <div className="flex items-center space-x-2">
              <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                {activeFiltersCount} filter{activeFiltersCount !== 1 ? 's' : ''} active
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                className="text-xs"
              >
                Clear all
              </Button>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {selectedCount > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">
                {selectedCount} selected
              </span>
              <div className="relative">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBulkActions(!showBulkActions)}
                >
                  <Icon name="MoreHorizontal" size={16} className="mr-1" />
                  Actions
                </Button>
                
                {showBulkActions && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowBulkActions(false)}
                    />
                    <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-lg shadow-elevation-2 z-50">
                      <div className="py-1">
                        {bulkActions?.map((action) => (
                          <button
                            key={action?.value}
                            onClick={() => handleBulkActionSelect(action?.value)}
                            className="flex items-center w-full px-3 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                          >
                            <Icon name={action?.icon} size={16} className="mr-2" />
                            {action?.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="lg:hidden"
          >
            <Icon name="Filter" size={16} className="mr-1" />
            Filters
            <Icon 
              name="ChevronDown" 
              size={16} 
              className={`ml-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            />
          </Button>
        </div>
      </div>
      {/* Filters */}
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 ${isExpanded ? 'block' : 'hidden lg:grid'}`}>
        <Input
          type="search"
          placeholder="Search deals..."
          value={filters?.search || ''}
          onChange={(e) => handleFilterChange('search', e?.target?.value)}
          className="lg:col-span-2"
        />
        
        <Select
          placeholder="Stage"
          options={stageOptions}
          value={filters?.stage || ''}
          onChange={(value) => handleFilterChange('stage', value)}
        />
        
        <Select
          placeholder="Owner"
          options={ownerOptions}
          value={filters?.owner || ''}
          onChange={(value) => handleFilterChange('owner', value)}
          searchable
        />
        
        <div className="flex space-x-2">
          <Input
            type="number"
            placeholder="Min value"
            value={filters?.minValue || ''}
            onChange={(e) => handleFilterChange('minValue', e?.target?.value)}
            className="flex-1"
          />
          <Input
            type="number"
            placeholder="Max value"
            value={filters?.maxValue || ''}
            onChange={(e) => handleFilterChange('maxValue', e?.target?.value)}
            className="flex-1"
          />
        </div>
      </div>
      {/* Advanced Filters Toggle */}
      <div className="hidden lg:flex items-center justify-between mt-4 pt-4 border-t border-border">
        <div className="flex items-center space-x-4">
          <Input
            type="date"
            placeholder="Close date from"
            value={filters?.closeDateFrom || ''}
            onChange={(e) => handleFilterChange('closeDateFrom', e?.target?.value)}
          />
          <Input
            type="date"
            placeholder="Close date to"
            value={filters?.closeDateTo || ''}
            onChange={(e) => handleFilterChange('closeDateTo', e?.target?.value)}
          />
        </div>
        
        <Button variant="outline" size="sm">
          <Icon name="Download" size={16} className="mr-1" />
          Export All
        </Button>
      </div>
    </div>
  );
};

export default DealsFilters;